import { revealPhoneChoices } from './enquiry-phone-fit'
import { api } from './api'
import { getMe } from './me'
import { phoneClock, screenLocale } from './round-config'
import { bubbleOf, type Thread } from './screens/chats'
import type { Lead } from './types'
import { el } from './ui'

type History = { last: number; thread?: Thread; pending?: Promise<Thread>; scrollTop?: number; followLatest?: boolean }
const histories = new Map<string, History>()
let active: { lead: string; history: HTMLElement; saved: History } | null = null
const PHONE_REPLIES = 'Replies to your phone: paste them with They replied'

export function releaseEnquiryChat(): void { active = null }
export function forgetEnquiryChats(): void { releaseEnquiryChat(); histories.clear() }

// Reuse Chats' conversation and media, without mounting Chats or calling /seen. The newest message id is a cache
// revision only, never a read mark. Keep snapshots within this visit to Messages and this signed-in business/owner.
export function enquiryChat(l: Lead, pasted: Map<number, HTMLElement | undefined>, fallback?: HTMLElement, tail?: HTMLElement, question?: (message: { body: string; at: string } | null | undefined) => void): HTMLElement {
  const me = getMe()
  const lead = `${me?.businessId}:${me?.email}:${l.id}`
  const last = l.last_message_id ?? l.last_in_id ?? 0
  let saved = histories.get(lead)
  if (!saved || saved.last !== last) { saved = { last }; histories.set(lead, saved) }
  const entry = saved
  if (active?.lead !== lead) entry.scrollTop = undefined
  else if (active.history.isConnected) active.saved.scrollTop = active.history.scrollTop

  const note = el('p', { class: 'enquiry-chat-note' }, PHONE_REPLIES)
  const history = el('div', { class: 'enquiry-chat-history', 'aria-label': `Chat history with ${l.name}` })
  // Choices and the paste form share the phone's scroller, but never its redrawn history.
  const scroller = tail ? el('div', { class: 'enquiry-chat-scroll' }, history, tail) : history
  const wrap = el('section', { class: 'enquiry-desk-chat' },
    el('header', { class: 'enquiry-chat-head' }, el('h3', {}, `Chat with ${l.name.split(' ')[0] || l.name}`), note), scroller)
  active = { lead, history: scroller, saved: entry }
  if (tail) scroller.dataset.followLatest = entry.scrollTop === undefined || entry.followLatest ? '1' : '0'
  scroller.addEventListener('scroll', () => {
    entry.scrollTop = scroller.scrollTop
    if (tail) { entry.followLatest = Math.abs(scroller.scrollTop - Number(scroller.dataset.latestTop ?? 0)) < 8; scroller.dataset.followLatest = entry.followLatest ? '1' : '0' }
  }, { passive: true })

  const show = (t: Thread, earlierPosition?: { top: number; height: number }): void => {
    const asked = t.messages.filter(m => m.direction === 'in' && m.body?.trim()).at(-1)
    question?.(asked?.body ? { body: asked.body, at: asked.at } : null)
    history.replaceChildren()
    if (t.more && t.messages.length) {
      const earlier = el('button', { type: 'button', class: 'link-button' }, 'Earlier messages') as HTMLButtonElement
      earlier.addEventListener('click', () => { earlier.disabled = true; void read(t.messages[0].id, earlier) })
      history.append(earlier)
    }
    for (const m of t.messages) {
      const manual = m.direction === 'in' && pasted.has(m.id)
      const off = manual ? pasted.get(m.id) : undefined
      const date = new Date(`${m.at.replace(' ', 'T')}Z`)
      const time = Number.isNaN(date.getTime()) ? m.at : `${date.toLocaleDateString(screenLocale(), { day: 'numeric', month: 'short' })}, ${phoneClock(date)}`
      const way = m.way === 'whatsapp' ? 'WhatsApp' : m.way === 'form' ? 'website form' : m.way === 'call' ? 'phone call' : m.way
      const row = el('div', { class: `enquiry-chat-message ${m.direction === 'out' ? 'enquiry-chat-mine' : 'enquiry-chat-theirs'}`, 'data-chat-message': String(m.id) },
        ...bubbleOf(t, m), el('span', { class: 'enquiry-chat-meta' }, `${manual ? 'You pasted this · ' : m.direction === 'out' && m.source === 'phone' ? 'Sent by you · ' : ''}${time} · by ${way}${m.direction === 'out' && m.state === 'failed' ? " · Didn't go" : ''}`))
      if (off) row.append(off)
      history.append(row)
    }
    if (fallback && !t.messages.some((m) => m.direction === 'in' && m.body === fallback.querySelector('.rebook-preview-text')?.textContent)) history.append(fallback)
    if (!t.messages.length && !fallback) history.append(el('p', { class: 'muted' }, 'No messages kept yet.'))
    // This card sends through the owner's own apps. Connected Roundly channels are separate reply destinations.
    const connected = [t.ways.text ? 'Text replies to your Roundly number arrive here by themselves.' : '',
      t.ways.email ? 'Email replies to your Roundly quotes address arrive here by themselves.' : '',
      t.ways.whatsapp ? 'WhatsApp replies to your connected work number arrive here by themselves.' : ''].filter(Boolean)
    note.textContent = connected.length ? `${PHONE_REPLIES}. ${connected.join(' ')}` : PHONE_REPLIES
    requestAnimationFrame(() => {
      if (!wrap.isConnected) return
      if (tail?.contains(document.activeElement) && !earlierPosition) {
        entry.scrollTop = scroller.scrollTop; entry.followLatest = false; scroller.dataset.followLatest = '0'
        return
      }
      scroller.scrollTop = earlierPosition ? earlierPosition.top + scroller.scrollHeight - earlierPosition.height : entry.scrollTop ?? scroller.scrollHeight
      if (tail && !earlierPosition && scroller.dataset.followLatest === '1') revealPhoneChoices(scroller)
      entry.scrollTop = scroller.scrollTop
    })
  }
  const read = async (before?: number, retry?: HTMLButtonElement): Promise<void> => {
    const position = before ? { top: scroller.scrollTop, height: scroller.scrollHeight } : undefined
    try {
      if (before) {
        const t = await api<Thread>(`/api/chats/lead/${l.id}?before=${before}`)
        entry.thread = { ...t, messages: [...t.messages, ...(entry.thread?.messages ?? [])] }
      } else {
        // Redraws during the first request share it as well as sharing the completed snapshot.
        entry.pending ??= api<Thread>(`/api/chats/lead/${l.id}`).then((t) => { entry.thread = t; return t }).finally(() => { entry.pending = undefined })
        await entry.pending
      }
      if (wrap.isConnected && entry.thread) show(entry.thread, position)
    } catch {
      if (!wrap.isConnected) return
      if (!entry.thread) question?.(undefined)
      loading.remove()
      const again = retry ?? el('button', { type: 'button', class: 'link-button' }) as HTMLButtonElement
      again.disabled = false
      again.textContent = 'Could not get the conversation. Try again'
      if (!retry) { again.addEventListener('click', () => { again.disabled = true; void read(undefined, again) }); history.prepend(again) }
    }
  }
  const loading = el('p', { class: 'muted' }, 'Getting the conversation…')
  if (entry.thread) show(entry.thread)
  else {
    history.append(loading)
    if (fallback) history.append(fallback)
    void read()
  }
  return wrap
}
